import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Stack,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Button,
  Pagination,
  Paper,
  LinearProgress,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { createConcurrencyLimiter } from '../../utils/concurrentLimit';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Device as DeviceModel } from './types';

interface FtpUpload {
  fileName: string;
  relativePath: string;
  sizeBytes: number;
  createdDate: string;
  hasDetection: boolean;
  detectionEventId?: string;
  detectionData?: {
    detection_type: string;
    detection_count: number;
    confidence_threshold: number;
    detections: Array<{
      class_id: number;
      class_name: string;
      confidence: number;
      bbox: [number, number, number, number]; // [x1, y1, x2, y2]
    }>;
    class_indices: string;
    model_type: string;
    timestamp: string;
    source_image: string;
  };
}

interface DeviceUploadsTabProps {
  deviceId: string;
  childDevices?: DeviceModel[];
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const DeviceUploadsTab: React.FC<DeviceUploadsTabProps> = ({ deviceId, childDevices = [] }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const [uploads, setUploads] = useState<FtpUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCamera, setSelectedCamera] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedUpload, setSelectedUpload] = useState<FtpUpload | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [deletePageConfirmOpen, setDeletePageConfirmOpen] = useState(false);
  const [deletingPage, setDeletingPage] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [uploadToDelete, setUploadToDelete] = useState<FtpUpload | null>(null);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Test image injection
  const [injectFile, setInjectFile] = useState<File | null>(null);
  const [injectTarget, setInjectTarget] = useState<string>('parent');
  const [injectLoading, setInjectLoading] = useState(false);
  const [injectStatus, setInjectStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const injectInputRef = useRef<HTMLInputElement>(null);

  const authHeader = { Authorization: `Bearer ${accessToken}` };

  useEffect(() => {
    loadUploads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, selectedCamera, startDate, endDate, page, pageSize]);

  // When the uploads list changes (page/filter change), revoke old blob URLs and load fresh thumbnails
  useEffect(() => {
    if (uploads.length === 0) return;
    // Revoke previous page's blob URLs to free memory
    setThumbnails(prev => {
      Object.values(prev).forEach(url => URL.revokeObjectURL(url));
      return {};
    });

    let cancelled = false;
    const limit = createConcurrencyLimiter(6);
    const token = { Authorization: `Bearer ${accessToken}` };

    Promise.allSettled(
      uploads.map(upload =>
        limit(async () => {
          if (cancelled) return;
          const res = await fetch(
            `/api/devices/${deviceId}/ftps-uploads/file?filename=${encodeURIComponent(upload.relativePath)}&maxWidth=150`,
            { headers: token }
          );
          if (!res.ok || cancelled) return;
          const blob = await res.blob();
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          setThumbnails(prev => ({ ...prev, [upload.fileName]: url }));
        })
      )
    );

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads]);

  // Revoke all remaining blob URLs on unmount
  useEffect(() => {
    return () => {
      setThumbnails(prev => {
        Object.values(prev).forEach(url => URL.revokeObjectURL(url));
        return {};
      });
    };
  }, []);

  const loadUploads = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        skip: ((page - 1) * pageSize).toString(),
        take: pageSize.toString(),
      });

      if (selectedCamera !== 'all') {
        params.append('childDeviceId', selectedCamera);
      }
      if (startDate) {
        params.append('startDate', startDate);
      }
      if (endDate) {
        params.append('endDate', endDate);
      }

      const res = await fetch(`/api/devices/${deviceId}/ftps-uploads?${params}`, {
        headers: authHeader,
      });

      if (!res.ok) {
        throw new Error('Failed to load uploads');
      }

      const data = await res.json();
      setUploads(data.data || []);
      setTotalCount(data.total || 0);
    } catch (err: any) {
      setError(err.message || 'An error occurred while loading uploads');
    } finally {
      setLoading(false);
    }
  };

  const openViewer = async (upload: FtpUpload) => {
    setSelectedUpload(upload);
    setViewerOpen(true);

    try {
      // Load full-size image
      const res = await fetch(`/api/devices/${deviceId}/ftps-uploads/file?filename=${encodeURIComponent(upload.relativePath)}`, {
        headers: authHeader,
      });

      if (!res.ok) {
        throw new Error('Failed to load image');
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setImageUrl(url);
    } catch (err) {
      console.error('Error loading image:', err);
      setError('Failed to load image');
    }
  };

  const closeViewer = () => {
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
      setImageUrl(null);
    }
    setViewerOpen(false);
    setSelectedUpload(null);
  };

  // Draw bounding boxes on canvas
  useEffect(() => {
    if (!viewerOpen || !imageUrl || !canvasRef.current || !imgRef.current) {
      return;
    }

    const img = imgRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return;

    const drawImage = () => {
      // Set canvas size to match image
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      // Draw the image
      ctx.drawImage(img, 0, 0);

      // Draw bounding boxes if we have detection data
      if (selectedUpload?.hasDetection && selectedUpload.detectionData?.detections) {
        const detections = selectedUpload.detectionData.detections;

        detections.forEach((detection) => {
          const [x1, y1, x2, y2] = detection.bbox;
          const width = x2 - x1;
          const height = y2 - y1;

          // Draw box
          ctx.strokeStyle = '#00ff00';
          ctx.lineWidth = 3;
          ctx.strokeRect(x1, y1, width, height);

          // Draw label background
          const label = `${detection.class_name} ${(detection.confidence * 100).toFixed(1)}%`;
          ctx.font = '16px Arial';
          const textMetrics = ctx.measureText(label);
          const textHeight = 20;

          ctx.fillStyle = 'rgba(0, 255, 0, 0.3)';
          ctx.fillRect(x1, y1 - textHeight - 4, textMetrics.width + 8, textHeight + 4);

          // Draw label text
          ctx.fillStyle = '#00ff00';
          ctx.fillText(label, x1 + 4, y1 - 8);
        });
      }
    };

    if (img.complete) {
      drawImage();
    } else {
      img.onload = drawImage;
    }
  }, [viewerOpen, imageUrl, selectedUpload]);

  const downloadImage = async () => {
    if (!selectedUpload) return;

    try {
      const res = await fetch(`/api/devices/${deviceId}/ftps-uploads/file?filename=${encodeURIComponent(selectedUpload.relativePath)}`, {
        headers: authHeader,
      });

      if (!res.ok) throw new Error('Download failed');

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedUpload.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading image:', err);
    }
  };

  const exportDetectionData = async () => {
    if (!selectedUpload?.detectionData) return;

    // Create JSON file
    const dataStr = JSON.stringify(selectedUpload.detectionData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedUpload.fileName}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const confirmDelete = (upload: FtpUpload) => {
    setUploadToDelete(upload);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!uploadToDelete) return;

    try {
      const res = await fetch(
        `/api/devices/${deviceId}/ftps-uploads/file?filename=${encodeURIComponent(uploadToDelete.relativePath)}`,
        { method: 'DELETE', headers: authHeader }
      );

      if (!res.ok) throw new Error('Delete failed');

      // Refresh the list
      await loadUploads();
      setDeleteConfirmOpen(false);
      setUploadToDelete(null);
    } catch (err) {
      console.error('Error deleting image:', err);
      setError('Failed to delete image');
    }
  };

  // Deletes every upload matching the currently active filters — i.e. exactly what
  // "totalCount" and the filtered list represent, not literally every upload the device
  // has ever received. Selecting a specific camera scopes this to that camera; "All
  // Cameras" scopes it to this device and its children (previous "delete all" behaviour).
  const handleDeleteAll = async () => {
    setDeletingAll(true);
    try {
      const params = new URLSearchParams();
      if (selectedCamera !== 'all') params.append('childDeviceId', selectedCamera);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const res = await fetch(
        `/api/devices/${deviceId}/ftps-uploads/all?${params}`,
        { method: 'DELETE', headers: authHeader }
      );

      if (!res.ok) {
        if (res.status === 403) {
          setError('Only administrators can delete all images');
        } else {
          throw new Error('Delete all failed');
        }
        setDeleteAllConfirmOpen(false);
        return;
      }

      setDeleteAllConfirmOpen(false);
      // The filtered set is now empty — start back at page 1 rather than reloading
      // whatever page number we happened to be on, which would show as empty.
      if (page !== 1) {
        setPage(1); // triggers reload via the loadUploads effect
      } else {
        await loadUploads();
      }
    } catch (err) {
      console.error('Error deleting all images:', err);
      setError('Failed to delete all images');
    } finally {
      setDeletingAll(false);
    }
  };

  // Deletes exactly the files currently loaded into `uploads` — the page the user is
  // actually looking at — by sending their filenames explicitly, rather than asking the
  // server to recompute "this page" from skip/take (which could drift if new uploads
  // arrive between load and delete). This is what keeps "delete this page" from ever
  // turning into "delete everything".
  const handleDeletePage = async () => {
    if (uploads.length === 0) return;
    setDeletingPage(true);
    try {
      const filenames = uploads.map((u) => u.relativePath);
      const res = await fetch(`/api/devices/${deviceId}/ftps-uploads/batch-delete`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ filenames }),
      });

      if (!res.ok) throw new Error('Delete page failed');

      setDeletePageConfirmOpen(false);
      // If deleting this page emptied out everything after it, step back a page instead
      // of reloading into what's now blank space.
      const newTotal = Math.max(0, totalCount - filenames.length);
      const newTotalPages = Math.max(1, Math.ceil(newTotal / pageSize));
      if (page > newTotalPages) {
        setPage(newTotalPages); // triggers reload via the loadUploads effect
      } else {
        await loadUploads();
      }
    } catch (err) {
      console.error('Error deleting page of images:', err);
      setError('Failed to delete images on this page');
    } finally {
      setDeletingPage(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // Check if user is admin
  const roles = (auth.user as any)?.profile?.role;
  const isAdmin = Array.isArray(roles) ? roles.includes('DMO_ADMIN') : roles === 'DMO_ADMIN';

  // Human-readable description of what "Delete All" currently scopes to, so the
  // confirmation dialog can't be misread as "wipe every upload this device has ever had"
  // when a camera or date filter is narrowing it down.
  const selectedCameraName = selectedCamera === 'all' ? null : childDevices.find((d) => d.id === selectedCamera)?.name;
  const deleteAllScopeText = selectedCameraName ? `the camera "${selectedCameraName}"` : 'this device and its child cameras';
  const deleteAllDateRangeText = startDate || endDate
    ? ` within the selected date range (${startDate || 'earliest'} to ${endDate || 'latest'})`
    : '';

  const handleInjectUpload = async () => {
    if (!injectFile) return;
    setInjectLoading(true);
    setInjectStatus(null);
    try {
      const form = new FormData();
      form.append('file', injectFile);
      const targetId = injectTarget === 'parent' ? null : injectTarget;
      const url = `/api/devices/${deviceId}/ftps-uploads/inject${targetId ? `?targetDeviceId=${targetId}` : ''}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeader,
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(err.message || 'Upload failed');
      }
      const data = await res.json();
      setInjectStatus({ type: 'success', message: `${data.fileName} written — inference will run within seconds.` });
      setInjectFile(null);
      if (injectInputRef.current) injectInputRef.current.value = '';
      // Refresh list after a short delay so the image processor can process
      setTimeout(() => loadUploads(), 8000);
    } catch (err: any) {
      setInjectStatus({ type: 'error', message: err.message || 'Upload failed' });
    } finally {
      setInjectLoading(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }} spacing={1} flexWrap="wrap">
        <Typography variant="h5">
          FTP Uploads
        </Typography>
        <Stack direction="row" spacing={1}>
          {uploads.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeletePageConfirmOpen(true)}
            >
              Delete This Page ({uploads.length})
            </Button>
          )}
          {isAdmin && totalCount > 0 && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteAllConfirmOpen(true)}
            >
              {selectedCamera === 'all' ? 'Delete All Images' : 'Delete All for Camera'}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Test image injection panel */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUploadIcon fontSize="small" /> Upload Test Image for Inference
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField
            select
            label="Target Device"
            value={injectTarget}
            onChange={(e) => setInjectTarget(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="parent">Parent Device (this device)</MenuItem>
            {childDevices.map((d) => (
              <MenuItem key={d.id} value={d.id!}>{d.name}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            component="label"
            size="small"
          >
            {injectFile ? injectFile.name : 'Choose Image…'}
            <input
              ref={injectInputRef}
              type="file"
              hidden
              accept="image/jpeg,image/jpg,image/png"
              onChange={(e) => {
                setInjectFile(e.target.files?.[0] ?? null);
                setInjectStatus(null);
              }}
            />
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={!injectFile || injectLoading}
            onClick={handleInjectUpload}
            startIcon={injectLoading ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon />}
          >
            {injectLoading ? 'Uploading…' : 'Upload & Infer'}
          </Button>
        </Stack>
        {injectLoading && <LinearProgress sx={{ mt: 1 }} />}
        {injectStatus && (
          <Alert severity={injectStatus.type} sx={{ mt: 1 }} onClose={() => setInjectStatus(null)}>
            {injectStatus.message}
          </Alert>
        )}
      </Paper>

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          select
          label="Camera"
          value={selectedCamera}
          onChange={(e) => {
            setSelectedCamera(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">All Cameras</MenuItem>
          {childDevices.map((device) => (
            <MenuItem key={device.id} value={device.id}>
              {device.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Per page"
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          sx={{ minWidth: 110 }}
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <MenuItem key={size} value={size}>{size}</MenuItem>
          ))}
        </TextField>

        <TextField
          type="date"
          label="Start Date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          type="date"
          label="End Date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
        />

        {(selectedCamera !== 'all' || startDate || endDate) && (
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedCamera('all');
              setStartDate('');
              setEndDate('');
              setPage(1);
            }}
          >
            Clear Filters
          </Button>
        )}
      </Stack>

      {/* Results summary */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {totalCount} upload{totalCount !== 1 ? 's' : ''} found
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Image Grid */}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(125px, 1fr))' },
            }}
          >
            {uploads.map((upload) => (
              <Card
                key={upload.fileName}
                sx={{
                  cursor: 'pointer',
                  position: 'relative',
                  '&:hover': { boxShadow: 4 },
                }}
                onClick={() => openViewer(upload)}
              >
                <CardMedia
                  component="img"
                  image={thumbnails[upload.fileName] || ''}
                  alt={upload.fileName}
                  sx={{ height: { xs: 220, sm: 125 }, objectFit: 'cover', bgcolor: thumbnails[upload.fileName] ? 'transparent' : 'grey.200' }}
                />

                {/* Detection badge */}
                {upload.hasDetection && upload.detectionData && (
                  <Chip
                    label={`${upload.detectionData.detection_count} ${upload.detectionData.detection_type}`}
                    size="small"
                    color="success"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                    }}
                  />
                )}

                <CardContent sx={{ p: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" noWrap title={upload.fileName}>
                        {upload.fileName}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {new Date(upload.createdDate).toLocaleString()}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete(upload);
                      }}
                      sx={{ ml: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}

          {uploads.length === 0 && !loading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No uploads found. Images uploaded via FTP will appear here.
            </Alert>
          )}
        </>
      )}

      {/* Image Viewer Dialog */}
      <Dialog
        open={viewerOpen}
        onClose={closeViewer}
        maxWidth="xl"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
          <IconButton
            onClick={closeViewer}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Action buttons */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 1,
            }}
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={downloadImage}
              sx={{ bgcolor: 'rgba(0,0,0,0.7)' }}
            >
              Download
            </Button>
            {selectedUpload?.hasDetection && (
              <Button
                variant="contained"
                size="small"
                onClick={exportDetectionData}
                sx={{ bgcolor: 'rgba(0,0,0,0.7)' }}
              >
                Export JSON
              </Button>
            )}
          </Stack>

          {/* Image info */}
          {selectedUpload && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: 'rgba(0,0,0,0.7)',
                color: 'white',
                p: 2,
                zIndex: 1,
              }}
            >
              <Typography variant="subtitle1">{selectedUpload.fileName}</Typography>
              <Typography variant="caption" display="block">
                {new Date(selectedUpload.createdDate).toLocaleString()} • {(selectedUpload.sizeBytes / 1024).toFixed(1)} KB
              </Typography>
              {selectedUpload.hasDetection && selectedUpload.detectionData && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip
                    label={`Type: ${selectedUpload.detectionData.detection_type}`}
                    size="small"
                    color="success"
                  />
                  <Chip
                    label={`Count: ${selectedUpload.detectionData.detection_count}`}
                    size="small"
                    color="info"
                  />
                  <Chip
                    label={`Confidence: ${selectedUpload.detectionData.confidence_threshold}`}
                    size="small"
                  />
                  <Chip
                    label={`Model: ${selectedUpload.detectionData.model_type}`}
                    size="small"
                  />
                </Stack>
              )}
            </Box>
          )}

          {/* Canvas for rendering image with bounding boxes */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '60vh',
              maxHeight: '85vh',
              overflow: 'auto',
            }}
          >
            {imageUrl && (
              <>
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt={selectedUpload?.fileName}
                  style={{ display: 'none' }}
                />
                <canvas
                  ref={canvasRef}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '85vh',
                    objectFit: 'contain',
                  }}
                />
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{uploadToDelete?.fileName}</strong>?
            {uploadToDelete?.hasDetection && ' This will also delete the associated detection data.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete This Page Confirmation Dialog */}
      <Dialog open={deletePageConfirmOpen} onClose={() => setDeletePageConfirmOpen(false)}>
        <DialogTitle>Delete This Page</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the <strong>{uploads.length} image{uploads.length !== 1 ? 's' : ''} shown on this page</strong> (page {page} of {totalPages || 1})?
            Uploads on other pages will not be affected. This action cannot be undone and will also delete any associated detection data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePageConfirmOpen(false)} disabled={deletingPage}>Cancel</Button>
          <Button onClick={handleDeletePage} color="error" variant="contained" disabled={deletingPage}>
            {deletingPage ? 'Deleting…' : 'Delete This Page'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog open={deleteAllConfirmOpen} onClose={() => setDeleteAllConfirmOpen(false)}>
        <DialogTitle>Delete All Images</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>all {totalCount} image{totalCount !== 1 ? 's' : ''}</strong> for {deleteAllScopeText}{deleteAllDateRangeText}?
            This action cannot be undone and will also delete all associated detection data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllConfirmOpen(false)} disabled={deletingAll}>Cancel</Button>
          <Button onClick={handleDeleteAll} color="error" variant="contained" disabled={deletingAll}>
            {deletingAll ? 'Deleting…' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Page Confirmation Dialog */}
      <Dialog open={deletePageConfirmOpen} onClose={() => setDeletePageConfirmOpen(false)}>
        <DialogTitle>Delete Page</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the <strong>{uploads.length} image{uploads.length !== 1 ? 's' : ''}</strong> shown on this page?
            This action cannot be undone and will also delete any associated detection data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePageConfirmOpen(false)} disabled={deletingPage}>Cancel</Button>
          <Button onClick={handleDeletePage} color="error" variant="contained" disabled={deletingPage}>
            {deletingPage ? 'Deleting…' : 'Delete Page'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceUploadsTab;
